import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader, Inbox, Mail, Copy, Check, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

export const AdminContacts = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You need to be signed in as an admin to view this page.');
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to load submissions.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && (!user || user === false)) {
      navigate('/login');
    } else if (user) {
      fetchSubmissions();
    }
  }, [user, authLoading, navigate, fetchSubmissions]);

  const copyEmail = (email, id) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center" data-testid="admin-loading">
        <Loader className="w-7 h-7 accent-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)]" data-testid="admin-contacts-page">
      <section className="relative py-14 px-4 sm:px-6 lg:px-8 hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="eyebrow">Admin · Inbox</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-3 text-strong tracking-tight" data-testid="admin-heading">
              Contact <span className="text-gradient-brand">Submissions</span>
            </h1>
            <p className="text-base text-muted">
              Every message sent through the contact form on the site.
            </p>
          </div>
          <button
            onClick={fetchSubmissions}
            className="btn-secondary px-4 py-2.5 inline-flex items-center space-x-2 text-sm"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </section>

      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-subtle">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div
              className="mb-8 p-4 rounded-lg border border-red-500/30 bg-red-500/5 text-sm text-red-300"
              data-testid="admin-error"
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-muted" data-testid="admin-total">
              {total} message{total === 1 ? '' : 's'}
            </span>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-20 card-surface" data-testid="admin-empty">
              <Inbox className="w-12 h-12 text-faded mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-strong">No submissions yet</h3>
              <p className="text-muted text-sm">When someone fills out the contact form, it&apos;ll show up here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="card-surface p-6"
                  data-testid={`admin-submission-${item.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-strong" data-testid={`admin-name-${item.id}`}>
                        {item.name || '(no name)'}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <Mail className="w-3.5 h-3.5 text-faded" />
                        <a
                          href={`mailto:${item.email}`}
                          className="text-sm text-default hover:accent-green transition-colors"
                          data-testid={`admin-email-${item.id}`}
                        >
                          {item.email}
                        </a>
                        <button
                          onClick={() => copyEmail(item.email, item.id)}
                          className="text-faded hover:text-strong transition-colors p-1"
                          aria-label="Copy email"
                          data-testid={`admin-copy-email-${item.id}`}
                        >
                          {copiedId === item.id ? <Check className="w-3.5 h-3.5 accent-green" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                    <span
                      className="text-xs text-faded"
                      data-testid={`admin-date-${item.id}`}
                    >
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                  <p
                    className="text-sm text-default whitespace-pre-wrap leading-relaxed"
                    data-testid={`admin-message-${item.id}`}
                  >
                    {item.message}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminContacts;
